import { Component, Input, computed, inject, signal } from '@angular/core';
import { ForexService } from '../services/forex';
import { AnalystRating, formatRatingPrice, ratingPricesAreSame } from '../analysts-data';

interface RatingDetailMetric {
  title: string;
  value?: string;
  positive?: boolean;
  subtext: string;
}

// Trefferquote/Ø Rendite formatting — same German-locale convention as PriceFormatPipe (2
// decimals, comma separator); a plain formatter since a computed()/pipe can't take a per-row
// argument.
const RATING_STAT_PERCENT_FORMATTER = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// A single analyst rating row — shared by V3AnalystsDesktopContent and V3AnalystsAnalysis's
// mobile ratings list so this ~150-line row never gets hand-copied twice. See CLAUDE.md.
@Component({
  selector: 'app-v3-analysts-rating-item',
  standalone: true,
  templateUrl: './v3-analysts-rating-item.html',
  styleUrl: './v3-analysts-rating-item.scss',
})
export class V3AnalystsRatingItem {
  private readonly forexService = inject(ForexService);

  @Input({ required: true }) rating!: AnalystRating;

  // Whether the row can expand at all — false only on V3AnalystsOverview's card.
  @Input() isDetail = false;

  stars(): boolean[] {
    if (this.rating.stars == null) return [];
    return Array.from({ length: 5 }, (_, i) => i < this.rating.stars!);
  }

  // "UBS | 08/10/2026" under the analyst's name.
  metaText(): string {
    return [this.rating.firm, this.rating.date].filter(Boolean).join(' | ');
  }

  // Matches AnalystRatingCard: hide the price entirely rather than showing "– → –" when both
  // values are missing.
  priceText(): string {
    const r = this.rating;
    if (r.priceFrom === null && r.priceTo === null) return '';
    if (ratingPricesAreSame(r.priceFrom, r.priceTo, r.currency, this.forexService)) {
      return formatRatingPrice(r.priceTo, r.currency, this.forexService);
    }
    return (
      `${formatRatingPrice(r.priceFrom, r.currency, this.forexService)} → ` +
      formatRatingPrice(r.priceTo, r.currency, this.forexService)
    );
  }

  // Per-analyst hit-rate/average-return stats (AI-596). Each metric only shows when its own
  // fields are present, rather than a placeholder.
  readonly detailMetrics = computed<RatingDetailMetric[]>(() => {
    const r = this.rating;
    const metrics: RatingDetailMetric[] = [];

    if (r.successRate != null && r.successfulRatings != null && r.totalRatings != null) {
      metrics.push({
        title: 'Trefferquote',
        value: `${Math.round(r.successRate * 100)} %`,
        positive: r.successRate >= 0.5,
        subtext: `${r.successfulRatings} von ${r.totalRatings} Einschätzungen trafen zu`,
      });
    }

    // Unlike successRate (a 0–1 fraction), TipRanks' excessReturn already arrives in percent.
    if (r.averageReturn != null) {
      const percent = r.averageReturn;
      metrics.push({
        title: 'Ø Rendite',
        value: `${percent >= 0 ? '+' : ''}${RATING_STAT_PERCENT_FORMATTER.format(percent)} %`,
        positive: percent >= 0,
        subtext: 'je Einschätzung',
      });
    }

    return metrics;
  });

  // Only expandable when isDetail AND there's actually something to show — otherwise expanding
  // would reveal an empty, dividerless box.
  readonly isExpandable = computed(() => this.isDetail && this.detailMetrics().length > 0);

  // Per-instance state — no need to key this by rating identity the way the old parent-tracked
  // Set did, since each rating now gets its own component instance (Angular resets/discards this
  // signal whenever @for's track key changes the underlying instance).
  private readonly expanded = signal(false);

  isExpanded(): boolean {
    return this.expanded();
  }

  // The whole row is the click/keyboard target (not just the chevron) — only interactive when
  // isExpandable, which also gates the row's tabindex/role/aria-expanded in the template.
  onRowActivate() {
    if (this.isExpandable()) {
      this.expanded.update((v) => !v);
    }
  }
}
